import { useQuery, useMutation } from "@tanstack/react-query";
import { getIntroVideo, startOnBoard } from "services/on-board";
import { useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import { useEffect, useRef, useState } from "react";
import { updateVisitedUserStatus } from "services/setting.service";
import { IVisitedStatus } from "interfaces/user.interface";
import Button from "components/button/button";
import { useSelector } from "react-redux";

const UserOnBoard: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state: any) => state.auth);

  // Check if user already has careers unlocked
  const { data: existingCareers, isSuccess: careersChecked } = useQuery({
    queryKey: ["CheckExistingCareers"],
    queryFn: () => startOnBoard(1),
    refetchOnWindowFocus: false,
    retry: 0,
  });

  // Auto-redirect if user already watched welcome video and has careers
  useEffect(() => {
    if (!careersChecked) return;
    const hasWatched = userInfo?.user?.profile?.welcome_video_watched;
    const hasCareers = (existingCareers?.count ?? 0) > 0;
    if (hasWatched && hasCareers) {
      console.log("[onboard] Already watched welcome video + has careers — skipping to videos");
      navigate("/on-boarding/start", { replace: true });
    } else if (hasWatched && !hasCareers) {
      console.log("[onboard] Already watched welcome video — skipping to career picker");
      navigate("/on-boarding/select-careers", { replace: true });
    }
  }, [careersChecked, existingCareers, userInfo, navigate]);

  const { data: instructionVideoInfo } = useQuery({
    queryKey: ["getInstructionVideo"],
    queryFn: () => getIntroVideo(),
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  const handlePlayPause = () => {
    setPlaying((prev) => !prev);
  };

  const videoRef = useRef<ReactPlayer>(null);

  const hasExistingCareers = (existingCareers?.count ?? 0) > 0;

  const handleUpdateVisitStatus = useMutation({
    mutationFn: (data: IVisitedStatus) => updateVisitedUserStatus(data),
    retry: 0,
    onSuccess: () => {
      // Skip career picker if careers are already unlocked
      if (hasExistingCareers) {
        navigate("/on-boarding/start");
      } else {
        navigate("/on-boarding/select-careers");
      }
    },
  });

  return (
    <div className="flex flex-col items-center bg-white min-h-[calc(100vh-56px)] px-4 py-12 overflow-auto">
      <div className="flex flex-col items-center gap-10 w-full max-w-[960px]">
        {/* Header */}
        <header className="flex flex-col items-center gap-3">
          <img
            src="/assets/brand/CareerSight Logo Web-01.png"
            alt="CareerSight"
            className="h-6 w-[218px] object-contain"
          />
          <h1 className="text-display-lg text-brand text-center">
            Welcome to CareerSight
          </h1>
          <p className="text-base font-normal leading-5 text-text-muted text-center">
            Catch a glimpse of your future
          </p>
        </header>

        {/* Video + Steps */}
        <div className="flex flex-col lg:flex-row gap-6 w-full">
          {/* Video */}
          <div className="relative flex-1 aspect-video rounded-lg overflow-hidden shadow-card bg-black">
            <ReactPlayer
              ref={videoRef}
              playing={playing}
              config={{
                file: {
                  attributes: {
                    controlsList: "nodownload",
                    autoPlay: false,
                    style: { width: "100%", height: "100%", objectFit: "cover" },
                  },
                },
              }}
              url={
                instructionVideoInfo
                  ? (instructionVideoInfo[0]?.video_url as string)
                  : " "
              }
              controls={true}
              onReady={() => {
                setPlaying(false);
                setReady(true);
              }}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => {
                setPlaying(false);
                videoRef.current?.seekTo(0);
              }}
              width="100%"
              height="100%"
              className="absolute inset-0 !w-full !h-full [&_video]:!w-full [&_video]:!h-full"
            />
            {!playing && ready && (
              <button
                onClick={handlePlayPause}
                className="w-16 h-16 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="32"
                  height="32"
                  fill="#213269"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-3 lg:w-[280px] shrink-0">
            {[
              { step: 1, title: "Choose your first five", desc: "Pick five careers you're most curious about, or let us choose for you." },
              { step: 2, title: "Watch each intro video", desc: "Get an inside look at each career path. Finish one video to unlock the next." },
              { step: 3, title: "Rank what you watched", desc: "Rate each of the five careers based on where you see your future." },
              { step: 4, title: "Explore your dashboard", desc: "Unlock more careers and go deeper. Your AI coach, Career Sherpa, is ready when you are." },
            ].map((item) => (
              <div key={item.step} className="flex gap-3 items-start bg-background rounded-lg p-3">
                <span className="w-[28px] h-[28px] shrink-0 rounded-full bg-brand text-white text-sm font-semibold flex items-center justify-center">
                  {item.step}
                </span>
                <div>
                  <p className="text-body-emphasis text-text-heading">{item.title}</p>
                  <p className="text-body-sm text-text-muted">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Button
          type="button"
          variant="primary"
          className="min-w-[125px] px-6 py-2.5 rounded-full"
          disabled={handleUpdateVisitStatus.isPending}
          onClick={() => {
            handleUpdateVisitStatus.mutate({ welcome_video_watched: true });
          }}
        >
          {handleUpdateVisitStatus.isPending ? "Loading..." : "Start"}
        </Button>
      </div>
    </div>
  );
};

export default UserOnBoard;
